"use client";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay, Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Star, Quote, ArrowLeft, ArrowRight } from "lucide-react";
import { useLocale } from "next-intl";
import { CommentsSection as CommentsType } from "@/types/home";
import { GoogleReviewsCache, GoogleReview } from "@/types/googleReviews";

interface GoogleReviewsSectionProps {
      data?: CommentsType;
      reviews?: GoogleReviewsCache | null;
}

const GoogleLogo = ({ size = 18 }: { size?: number }) => (
      <svg
            width={size}
            height={size}
            viewBox="0 0 48 48"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
      >
            <path
                  fill="#FFC107"
                  d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
                  fill="#FF3D00"
                  d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
                  fill="#4CAF50"
                  d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
                  fill="#1976D2"
                  d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
      </svg>
);

function StarRow({ rating, size = 14 }: { rating: number; size?: number }) {
      const full = Math.floor(rating);
      const half = rating - full >= 0.5;
      return (
            <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }).map((_, i) => {
                        if (i < full) {
                              return (
                                    <Star
                                          key={i}
                                          size={size}
                                          className="fill-amber-400 text-amber-400"
                                    />
                              );
                        }
                        if (i === full && half) {
                              return (
                                    <span key={i} className="relative inline-block" style={{ width: size, height: size }}>
                                          <Star size={size} className="text-amber-400 absolute inset-0" />
                                          <span className="absolute inset-0 overflow-hidden" style={{ width: "50%" }}>
                                                <Star size={size} className="fill-amber-400 text-amber-400" />
                                          </span>
                                    </span>
                              );
                        }
                        return <Star key={i} size={size} className="text-gray-300" />;
                  })}
            </div>
      );
}

const LOCALE_MAP: Record<string, string> = {
      tr: "tr-TR",
      en: "en-US",
      de: "de-DE",
      ru: "ru-RU",
};

function pickReviewText(review: GoogleReview, locale: string): string {
      if (locale === "tr" && review.originalLanguage === "tr" && review.originalText) {
            return review.originalText;
      }
      return review.text || review.originalText || "";
}

function getInitial(name: string): string {
      return (name || "?").trim().charAt(0).toUpperCase();
}

export default function GoogleReviewsSection({ data, reviews }: GoogleReviewsSectionProps) {
      const locale = useLocale();
      const list = reviews?.reviews || [];
      if (list.length === 0) return null;

      const rating = reviews?.rating ?? 0;
      const total = reviews?.userRatingCount ?? 0;
      const mapsUri = reviews?.googleMapsUri || "https://maps.google.com";
      const placeName = reviews?.placeName || "Wolistic Center";

      const formattedRating = rating.toFixed(1);
      const localeKey = LOCALE_MAP[locale] || "tr-TR";

      return (
            <section className="py-16 md:py-20 bg-white overflow-hidden relative">
                  <div className="container mx-auto px-4 md:px-8 relative z-10">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                              <div className="flex-1 min-w-0">
                                    {data?.badge && (
                                          <span className="inline-block py-1 px-3 rounded-full bg-[#A68F72]/10 text-[#A68F72] text-xs font-semibold tracking-wider uppercase mb-3 border border-[#A68F72]/20">
                                                {data.badge}
                                          </span>
                                    )}
                                    {data?.title && (
                                          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                                                {data.title}
                                          </h2>
                                    )}

                                    <a
                                          href={mapsUri}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-3 mt-2 group"
                                    >
                                          <GoogleLogo size={24} />
                                          <div className="flex flex-col">
                                                <span className="flex items-center gap-2 text-sm">
                                                      <span className="font-bold text-gray-900">
                                                            {formattedRating}
                                                      </span>
                                                      <StarRow rating={rating} size={15} />
                                                      <span className="text-gray-500">
                                                            ({total.toLocaleString(localeKey)})
                                                      </span>
                                                </span>
                                                <span className="text-[11px] text-gray-500 group-hover:text-[#A68F72] transition-colors">
                                                      Google Reviews · {placeName} →
                                                </span>
                                          </div>
                                    </a>
                              </div>

                              <div className="flex gap-2 self-start md:self-end">
                                    <button
                                          type="button"
                                          aria-label="Previous review"
                                          className="swiper-prev-gr w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] transition-all duration-300 cursor-pointer"
                                    >
                                          <ArrowLeft size={18} />
                                    </button>
                                    <button
                                          type="button"
                                          aria-label="Next review"
                                          className="swiper-next-gr w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-[#A68F72] hover:text-white hover:border-[#A68F72] transition-all duration-300 cursor-pointer"
                                    >
                                          <ArrowRight size={18} />
                                    </button>
                              </div>
                        </div>

                        <Swiper
                              modules={[Pagination, Autoplay, Navigation]}
                              spaceBetween={20}
                              slidesPerView={1.1}
                              loop={list.length > 3}
                              autoplay={{
                                    delay: 6000,
                                    disableOnInteraction: false,
                                    pauseOnMouseEnter: true,
                              }}
                              navigation={{
                                    prevEl: ".swiper-prev-gr",
                                    nextEl: ".swiper-next-gr",
                              }}
                              pagination={{
                                    clickable: true,
                                    bulletClass: "gr-bullet",
                                    bulletActiveClass: "gr-bullet-active",
                              }}
                              breakpoints={{
                                    640: { slidesPerView: 2.1 },
                                    1024: { slidesPerView: 3 },
                              }}
                              className="pb-12 overflow-visible!"
                        >
                              {list.map((r, idx) => (
                                    <SwiperSlide key={`${r.authorName}-${idx}`} className="h-auto">
                                          <article className="h-full flex flex-col bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_28px_rgb(0,0,0,0.06)] transition-all duration-500 p-6">
                                                <div className="flex items-start justify-between mb-4">
                                                      <div className="flex items-center gap-3">
                                                            {r.authorPhotoUrl ? (
                                                                  // eslint-disable-next-line @next/next/no-img-element
                                                                  <img
                                                                        src={r.authorPhotoUrl}
                                                                        alt={r.authorName}
                                                                        width={42}
                                                                        height={42}
                                                                        className="w-10 h-10 rounded-full object-cover bg-gray-100"
                                                                        referrerPolicy="no-referrer"
                                                                        loading="lazy"
                                                                  />
                                                            ) : (
                                                                  <div className="w-10 h-10 rounded-full bg-[#A68F72]/15 text-[#A68F72] flex items-center justify-center font-bold text-sm">
                                                                        {getInitial(r.authorName)}
                                                                  </div>
                                                            )}
                                                            <div className="flex flex-col">
                                                                  <span className="text-sm font-semibold text-gray-900 line-clamp-1">
                                                                        {r.authorName}
                                                                  </span>
                                                                  <span className="text-[11px] text-gray-500">
                                                                        {r.relativeTime}
                                                                  </span>
                                                            </div>
                                                      </div>
                                                      <GoogleLogo size={18} />
                                                </div>
                                                <StarRow rating={r.rating} size={14} />
                                                <Quote
                                                      size={20}
                                                      className="text-[#A68F72]/30 mt-3 mb-1 shrink-0"
                                                />
                                                <p className="text-gray-600 text-sm leading-relaxed line-clamp-6 grow">
                                                      {pickReviewText(r, locale)}
                                                </p>
                                                {r.authorUri && (
                                                      <a
                                                            href={r.authorUri}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="mt-4 text-[11px] text-gray-400 hover:text-[#A68F72] self-start"
                                                      >
                                                            Google Profili →
                                                      </a>
                                                )}
                                          </article>
                                    </SwiperSlide>
                              ))}
                        </Swiper>
                  </div>

                  <style jsx global>{`
                        .gr-bullet {
                              display: inline-block;
                              width: 24px;
                              height: 3px;
                              border-radius: 9999px;
                              margin: 0 3px;
                              background: rgba(166, 143, 114, 0.25);
                              transition: background 0.3s, width 0.3s;
                              cursor: pointer;
                        }
                        .gr-bullet-active {
                              background: #a68f72;
                              width: 36px;
                        }
                  `}</style>
            </section>
      );
}
